import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Fade,
	IconButton,
	InputAdornment,
	Slide,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { HelpOutlineRounded } from '@mui/icons-material';

import './niceDialog.css';
import { Cache } from 'aws-amplify/utils';

const EXPIRATION_TIME_SECONDS = 300;

/**
 * Returns a time-of-day greeting based on the user's local browser time.
 */
function getGreeting() {
	const hour = new Date().getHours();
	if (hour < 12) return 'Good Morning';
	if (hour < 17) return 'Good Afternoon';
	return 'Good Evening';
}

/**
 * @typedef {Object} Props
 * @property {Function} onError The function to call when an error occurs.
 * @property {Function} onAgentConnected The function to call when the agent is successfully connected.
 */

/**
 * Displays an inline animated flow to verify the user's account for NICE.
 * Step 1: Greeting slides in, then agent ID form slides up.
 * Step 2: On submit, greeting + form fade out and verification code fades in.
 *
 * @param {Props} props The props for the component.
 */
export default function NiceDialog(props) {
	const userContext = useContext(UserContext);

	/** Whether something is currently being loaded in the page. */
	const [loading, setLoading] = useState(false);

	/** Whether the interface is being used within CXone Agent. If false, the interface is being used within MAX. */
	const [isCxOneAgent, setIsCxOneAgent] = useState(false);

	/**
	 * Current step of the inline flow:
	 * 'agentId' — show greeting + agent ID form
	 * 'verificationCode' — show verification code entry
	 * 'done' — connected, nothing to show
	 */
	const [step, setStep] = useState('agentId');

	/** Animation states */
	const [showGreeting, setShowGreeting] = useState(false);
	const [showAgentForm, setShowAgentForm] = useState(false);
	const [showCodeEntry, setShowCodeEntry] = useState(false);
	const [fadeOutAgentStep, setFadeOutAgentStep] = useState(false);

	/** The ID of the agent entered by the user. */
	const [agentId, setAgentId] = useState('');

	/** The skill IDs for the agent. */
	const [agentSkillIds, setAgentSkillIds] = useState([]);
	const [agentActiveSkillIds, setAgentActiveSkillIds] = useState([]);
	const [agentSkillNames, setAgentSkillNames] = useState({});
	const [agentFirstName, setAgentFirstName] = useState('');
	const [agentLastName, setAgentLastName] = useState('');

	/** The verification code entered by the user. */
	const [verificationCode, setVerificationCode] = useState('');

	/** The amount of time remaining before the verification code expires. */
	const [timeToCodeExpiration, setTimeToCodeExpiration] = useState('5:00');

	/** The number of seconds remaining before the verification code expires. */
	const [secondsToCodeExpiration, setSecondsToCodeExpiration] = useState(
		EXPIRATION_TIME_SECONDS,
	);

	/** Whether the help dialog is open. */
	const [helpOpen, setHelpOpen] = useState(false);

	const timeToCodeExpirationRef = useRef('5:00');
	const expirationIntervalRef = useRef();

	/**
	 * Determines whether the client is CXone Agent or MAX, then checks authorization.
	 */
	useEffect(() => {
		checkAuthorization();

		const urlParams = new URLSearchParams(window.location.search);
		const client = urlParams.get('client');
		if (client === 'agent') {
			setIsCxOneAgent(true);
		}
	}, []);

	/**
	 * Trigger the entrance animations sequentially when on the agentId step.
	 */
	useEffect(() => {
		if (step === 'agentId' && !loading) {
			// Slide in greeting after a short delay
			const greetingTimer = setTimeout(() => setShowGreeting(true), 200);
			// Slide up the form after the greeting slide finishes (200ms delay + 1200ms transition)
			const formTimer = setTimeout(() => setShowAgentForm(true), 1400);
			return () => {
				clearTimeout(greetingTimer);
				clearTimeout(formTimer);
			};
		}
	}, [step, loading]);

	useEffect(() => {
		timeToCodeExpirationRef.current = timeToCodeExpiration;
	}, [timeToCodeExpiration]);

	/**
	 * Checks if the user is already logged in to NICE.
	 */
	async function checkAuthorization() {
		const cachedAgentId = await Cache.getItem('niceAgentId');
		if (cachedAgentId) {
			const skillIds = await Cache.getItem('niceAgentSkills');
			const activeSkillIds = await Cache.getItem('niceAgentActiveSkills');
			const skillNames = await Cache.getItem('niceAgentSkillNames');
			const agentName = await Cache.getItem('niceAgentName');

			// If skills cache has expired or is empty, don't proceed with stale auth
			if (!skillIds || skillIds.length === 0) {
				console.debug('Cached agent has no skills — clearing cache and requiring re-auth.');
				await Cache.removeItem('niceAgentId');
				await Cache.removeItem('niceAgentSkills');
				await Cache.removeItem('niceAgentActiveSkills');
				await Cache.removeItem('niceAgentSkillNames');
				await Cache.removeItem('niceAgentName');
				setLoading(false);
				return;
			}

			// Immediately proceed with cached data to avoid UI flash
			console.debug('User has already been authorized with NICE.');
			setStep('done');
			props.onAgentConnected(cachedAgentId, skillIds, activeSkillIds || skillIds, skillNames || {}, agentName || '');
			setLoading(false);

			// Background refresh to apply hideInbound/hideOutbound settings
			try {
				const session = await fetchAuthSession();
				const response = await post({
					apiName: 'cdyxoutreach',
					path: '/approver/c2c-generate',
					options: {
						headers: {
							Authorization: `Bearer ${session.tokens.idToken}`,
							'x-api-key': userContext.apiKey,
						},
						body: {
							agentId: cachedAgentId,
							refreshOnly: true,
							hideInbound: props.hideInbound ?? true,
							hideOutbound: props.hideOutbound ?? false,
						},
					},
				}).response;
				const generateResponse = await response.body.json();
				const freshSkillIds = generateResponse.skills.map((skill) =>
					typeof skill === 'object' ? String(skill.skillId) : String(skill)
				);
				const freshActiveSkillIds = generateResponse.activeSkills
					? generateResponse.activeSkills.map((skill) =>
						typeof skill === 'object' ? String(skill.skillId) : String(skill)
					)
					: freshSkillIds;
				const freshSkillNames = {};
				for (const skill of generateResponse.skills) {
					if (typeof skill === 'object') {
						freshSkillNames[String(skill.skillId)] = skill.skillName || String(skill.skillId);
					}
				}

				// Update cache with fresh data
				const expireTime = new Date().getTime() + 3600000;
				await Cache.setItem('niceAgentSkills', freshSkillIds, { expires: expireTime });
				await Cache.setItem('niceAgentActiveSkills', freshActiveSkillIds, { expires: expireTime });
				if (Object.keys(freshSkillNames).length > 0) {
					await Cache.setItem('niceAgentSkillNames', freshSkillNames, { expires: expireTime });
				}

				// Notify parent again with fresh skills (will update UI if skills changed)
				props.onAgentConnected(cachedAgentId, freshSkillIds, freshActiveSkillIds, freshSkillNames, agentName || '');
			} catch (error) {
				console.warn('Background skill refresh failed, continuing with cached values', error);
			}
		}
	}

	/**
	 * Calls an API to generate a verification code and sends it to the agent.
	 * On success, fades out the agent step and fades in the code entry.
	 */
	async function sendVerificationCode() {
		setLoading(true);
		const session = await fetchAuthSession();
		const trimmedAgentId = agentId.trim();
		setAgentId(trimmedAgentId);

		try {
			const response = await post({
				apiName: 'cdyxoutreach',
				path: '/approver/c2c-generate',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						agentId: trimmedAgentId,
						hideInbound: props.hideInbound ?? true,
						hideOutbound: props.hideOutbound ?? false,
					},
				},
			}).response;
			const generateResponse = await response.body.json();
			const skillIds = generateResponse.skills.map((skill) =>
				typeof skill === 'object' ? String(skill.skillId) : String(skill)
			);
			const activeSkillIds = generateResponse.activeSkills
				? generateResponse.activeSkills.map((skill) =>
					typeof skill === 'object' ? String(skill.skillId) : String(skill)
				)
				: skillIds;
			const skillNameMap = {};
			for (const skill of generateResponse.skills) {
				if (typeof skill === 'object') {
					skillNameMap[String(skill.skillId)] = skill.skillName || String(skill.skillId);
				}
			}
			setAgentSkillIds(skillIds);
			setAgentActiveSkillIds(activeSkillIds);
			setAgentSkillNames(skillNameMap);
			setAgentFirstName(generateResponse.firstName || '');
			setAgentLastName(generateResponse.lastName || '');
		} catch (error) {
			setLoading(false);

			if (error.response && error.response.body === 'No skills retrieved for the agent') {
				props.onError(
					'No Skills',
					'You don\'t have any skills for your account. Please contact your administrator to assign you a skill.',
				);
				return;
			}

			props.onError(
				'Invalid Agent ID',
				'The agent ID you entered appears to be invalid. Please check the agent ID and try again.'
			);
			return;
		}

		setLoading(false);

		// Fade out greeting + agent form, then after transition show code entry
		setFadeOutAgentStep(true);
		setTimeout(() => {
			setStep('verificationCode');
			setShowCodeEntry(true);
		}, 500);

		// Start the expiration countdown
		expirationIntervalRef.current = setInterval(() => {
			setSecondsToCodeExpiration((prevSeconds) => prevSeconds - 1);
			const [minutes, seconds] = timeToCodeExpirationRef.current.split(':');
			if (minutes === '0' && seconds === '00') {
				clearInterval(expirationIntervalRef.current);
				return;
			}
			const newMinutes = seconds === '00' ? minutes - 1 : minutes;
			const newSeconds = seconds === '00' ? 59 : seconds - 1;
			const newTimeToCodeExpiration = `${newMinutes}:${newSeconds < 10 ? '0' + newSeconds : newSeconds}`;
			setTimeToCodeExpiration(newTimeToCodeExpiration);
		}, 1000);
	}

	/**
	 * Calls an API to verify the verification code entered by the user.
	 */
	async function verifyCode() {
		setLoading(true);

		let session;
		try {
			session = await fetchAuthSession();
		} catch (error) {
			console.error('Error getting current session', error);
			return;
		}

		try {
			await post({
				apiName: 'cdyxoutreach',
				path: '/approver/c2c-verify',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						verificationCode,
					},
				},
			}).response;
		} catch (error) {
			setLoading(false);
			props.onError(
				'Invalid Verification Code',
				'The verification code you entered is invalid. Please check the verification code and try again.',
			);
			return;
		}

		const expireTime = new Date().getTime() + 3600000;

		await Cache.setItem('niceAgentId', agentId, {
			expires: expireTime,
		});

		await Cache.setItem('niceAgentSkills', agentSkillIds, {
			expires: expireTime,
		});

		await Cache.setItem('niceAgentActiveSkills', agentActiveSkillIds, {
			expires: expireTime,
		});

		await Cache.setItem('niceAgentSkillNames', agentSkillNames, {
			expires: expireTime,
		});

		await Cache.setItem('niceAgentName', `${agentFirstName} ${agentLastName}`.trim(), {
			expires: expireTime,
		});

		setLoading(false);
		setStep('done');
		props.onAgentConnected(agentId, agentSkillIds, agentActiveSkillIds, agentSkillNames, `${agentFirstName} ${agentLastName}`.trim());
	}

	// If already connected or still loading initial check, render nothing (or a spinner)
	if (step === 'done') return null;

	return (
		<Box
			display="flex"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			sx={{ height: '100%', px: 3 }}
		>
			{/* ─── Step 1: Greeting + Agent ID Form ─── */}
			{step === 'agentId' && (
				<Fade in={!fadeOutAgentStep} timeout={450}>
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						sx={{ maxWidth: 400, width: '100%', position: 'relative', height: '100%' }}
					>
						{/* Greeting slides in from the left — fixed position so it doesn't shift */}
						<Box sx={{ position: 'absolute', top: '35%', transform: 'translateY(-50%)' }}>
							<Slide direction="right" in={showGreeting} timeout={1200} mountOnEnter unmountOnExit>
								<Typography
									variant="h3"
									color="text.primary"
									sx={{ fontWeight: 500, textAlign: 'center' }}
								>
									{getGreeting()}
								</Typography>
							</Slide>
						</Box>

						{/* Agent ID form slides up — positioned below the greeting */}
						<Box sx={{ position: 'absolute', top: '45%' }}>
							<Slide direction="up" in={showAgentForm} timeout={500} mountOnEnter unmountOnExit>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="center"
									sx={{ width: 320 }}
								>
									<Typography
										variant="body1"
										color="text.secondary"
										sx={{ mb: 2, textAlign: 'center' }}
									>
										Enter your Agent ID to get started.
									</Typography>

									<TextField
										margin="dense"
										name="agentId"
										label="Agent ID"
										placeholder="Enter your agent ID"
										type="text"
										autoCapitalize="off"
										autoCorrect="off"
										fullWidth
										value={agentId}
										onChange={(event) => setAgentId(event.target.value)}
										onKeyDown={(event) => {
											if (event.key === 'Enter' && agentId.length >= 1 && !loading) {
												sendVerificationCode();
											}
										}}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<Tooltip title="Where do I find this?">
														<IconButton onClick={() => setHelpOpen(true)}>
															<HelpOutlineRounded color="primary" />
														</IconButton>
													</Tooltip>
												</InputAdornment>
											),
										}}
										required
									/>

									<Button
										variant="contained"
										color="primary"
										onClick={sendVerificationCode}
										disabled={agentId.length < 1 || loading}
										sx={{ mt: 2, borderRadius: '7px', px: 4 }}
									>
										{loading ? <CircularProgress size={22} color="inherit" /> : 'Next'}
									</Button>
								</Box>
							</Slide>
						</Box>
					</Box>
				</Fade>
			)}

			{/* ─── Step 2: Verification Code (fades in) ─── */}
			{step === 'verificationCode' && (
				<Fade in={showCodeEntry} timeout={600}>
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						sx={{ maxWidth: 400, width: '100%' }}
					>
						<Typography variant="h5" sx={{ fontWeight: 500, mb: 1, textAlign: 'center' }}>
							Verification Code
						</Typography>

						<Typography variant="body1" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
							A verification code has been sent to you as a message in{' '}
							{isCxOneAgent ? 'CXone Agent' : 'MAX'}.
						</Typography>

						<Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
							Enter the code below to verify your account.
						</Typography>

						<TextField
							margin="dense"
							name="code"
							label="Verification Code"
							placeholder="Enter your verification code"
							helperText={
								timeToCodeExpiration === '0:00'
									? 'Your code has expired. Please reload and try again.'
									: `Your code will expire in ${timeToCodeExpiration}`
							}
							type="text"
							inputProps={{
								inputMode: 'numeric',
								maxLength: 6,
								pattern: '^[0-9]{6,}$',
							}}
							autoCorrect="off"
							autoComplete="off"
							value={verificationCode}
							onChange={(event) => setVerificationCode(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter' && verificationCode.length >= 6 && timeToCodeExpiration !== '0:00' && !loading) {
									verifyCode();
								}
							}}
							disabled={timeToCodeExpiration === '0:00'}
							InputProps={{
								startAdornment: (
									<CircularProgress
										size={20}
										variant="determinate"
										value={
											(secondsToCodeExpiration / EXPIRATION_TIME_SECONDS) * 100
										}
										color="primary"
										thickness={6}
										sx={{ marginRight: '10px' }}
									/>
								),
							}}
							fullWidth
							required
						/>

						<Button
							variant="contained"
							color="primary"
							onClick={verifyCode}
							disabled={
								verificationCode.length < 6 || timeToCodeExpiration === '0:00' || loading
							}
							sx={{ mt: 2, borderRadius: '7px', px: 4 }}
						>
							{loading ? <CircularProgress size={22} color="inherit" /> : 'Verify'}
						</Button>
					</Box>
				</Fade>
			)}

			{/* Loading spinner (only during initial auth check) */}
			{loading && step === 'agentId' && !showGreeting && (
				<CircularProgress variant="indeterminate" color="primary" />
			)}

			{/* ─── Agent ID Help Dialog (kept as a dialog since it's secondary info) ─── */}
			<Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth>
				<DialogTitle>
					Agent ID Help - {isCxOneAgent ? 'CXone Agent' : 'MAX'}
				</DialogTitle>
				<DialogContent>
					{isCxOneAgent && (
						<>
							<DialogContentText gutterBottom>
								Open the <em>Settings</em> tab from the tab bar at the left side
								of the window.
							</DialogContentText>
							<img
								src="/click2call-help/agent-settings.png"
								alt="Screenshot of the CXone Agent interface with settings highlighted"
								className="help-image"
							/>
							<DialogContentText gutterBottom>
								Select the <em>Information</em> from the tabs at the top of the
								content.
							</DialogContentText>
							<img
								src="/click2call-help/agent-information.png"
								alt="Screenshot of the CXone Agent interface with the Information tab highlighted"
								className="help-image"
							/>
							<DialogContentText>
								Copy the value listed as <em>Agent ID</em>.
							</DialogContentText>
							<img
								src="/click2call-help/agent-agent-id.png"
								alt="Screenshot of the CXone Agent interface with the Agent ID highlighted"
								className="help-image"
							/>
						</>
					)}
					{!isCxOneAgent && (
						<>
							<DialogContentText gutterBottom>
								Open the sidebar and select <em>More</em> at the bottom.
							</DialogContentText>
							<img
								src="/click2call-help/max-more.png"
								alt="Screen shot of MAX sidebar with the More option highlighted"
								className="help-image"
							/>
							<DialogContentText gutterBottom>
								Select <em>Information</em> from the options.
							</DialogContentText>
							<img
								src="/click2call-help/max-information.png"
								alt="Screenshot of MAX sidebar with the More Tools menu opened and the Information option highlighted"
								width={298}
								className="help-image"
							/>
							<DialogContentText>
								Copy the value listed as <em>Agent ID</em>.
							</DialogContentText>
							<img
								src="/click2call-help/max-agent-id.png"
								alt="Screenshot of the Information tab in MAX with the Agent ID highlighted"
								width={299}
								className="help-image"
							/>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button color="primary" onClick={() => setHelpOpen(false)}>
						OK
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
